import type { AnimationType, ReactZoomPanPinchContext, StateType } from "react-zoom-pan-pinch";

const handleCancelAnimationFrame = (animation: AnimationType | null) => {
  if (typeof animation === "number") {
    cancelAnimationFrame(animation);
  }
};

const handleCancelAnimation = (contextInstance: ReactZoomPanPinchContext): void => {
  if (!contextInstance.mounted) return;
  handleCancelAnimationFrame(contextInstance.animation);
  // Clear animation state
  contextInstance.animate = false;
  contextInstance.animation = null;
  contextInstance.velocity = null;
};

const animate = (contextInstance: ReactZoomPanPinchContext, targetState: StateType, animationTime: number): void => {
  const isValidTargetState = (targetState: StateType): boolean => {
    const { scale, positionX, positionY } = targetState;

    return !(Number.isNaN(scale) || Number.isNaN(positionX) || Number.isNaN(positionY));
  };

  const handleSetupAnimation = (
    contextInstance: ReactZoomPanPinchContext,
    animationTime: number,
    callback: (step: number) => void,
  ): void => {
    if (!contextInstance.mounted) return;
    const startTime = new Date().getTime();
    const lastStep = 1;

    handleCancelAnimation(contextInstance);

    contextInstance.animation = () => {
      if (!contextInstance.mounted) {
        return handleCancelAnimationFrame(contextInstance.animation);
      }

      const frameTime = new Date().getTime() - startTime;
      const animationProgress = frameTime / animationTime;
      const animationType = (t: number): number => {
        return -Math.cos(t * Math.PI) / 2 + 0.5;
      };
      const step = animationType(animationProgress);

      if (frameTime >= animationTime) {
        callback(lastStep);
        contextInstance.animation = null;
      } else if (contextInstance.animation) {
        callback(step);
        requestAnimationFrame(contextInstance.animation);
      }
    };

    requestAnimationFrame(contextInstance.animation);
  };

  const isValid = isValidTargetState(targetState);
  if (!contextInstance.mounted || !isValid) return;

  const { setTransformState } = contextInstance;
  const { scale, positionX, positionY } = contextInstance.transformState;

  const scaleDiff = targetState.scale - scale;
  const positionXDiff = targetState.positionX - positionX;
  const positionYDiff = targetState.positionY - positionY;

  if (animationTime === 0) {
    setTransformState(targetState.scale, targetState.positionX, targetState.positionY);
  } else {
    // animation start timestamp
    handleSetupAnimation(contextInstance, animationTime, (step: number) => {
      const newScale = scale + scaleDiff * step;
      const newPositionX = positionX + positionXDiff * step;
      const newPositionY = positionY + positionYDiff * step;

      setTransformState(newScale, newPositionX, newPositionY);
    });
  }
};

export { handleCancelAnimation, animate };
